import React, { useState, useEffect } from "react";
import styled from "styled-components";
import {
  RotaButton,
  RotaCheckbox,
  RotaDropdown,
  RotaInput,
  useToast
} from "@teamrota/rota-design";
import { useMutation } from "@apollo/client";
import { UPDATE_ROLE_RATE } from "./graphql/update-role-rate";

const ContentWrapper = styled.div`
  position: relative;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  margin-top: 15px;
  gap: 16px;
`;

export const ValueCell = styled.div`
  padding-left: 15px;
  color: ${({ theme }) => theme.text.primary};
  font-size: 16px;
  ${({ isCenterAligned }) =>
    isCenterAligned && `text-align: center; padding-left: 0; `}
`;

export const LabelCell = styled.div`
  padding: 15px;
  padding-bottom: 0;
  color: ${({ theme }) => theme.neutral.main};
  font-size: 14px;
  text-align: center;
`;

export const StyledGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  padding: 15px 0;
  overflow: none;
`;

export const StyledWrapSubtitle = styled.div`
  white-space: nowrap;
  font-size: 14px;
  border: 1px solid ${({ theme }) => theme.neutral.lighter};
  border-top-color: transparent;
  border-left-color: transparent;
  font-weight: bold;
  padding: 10px;
  display: flex;
  align-items: center;
`;

const DropdownWrapper = styled.div`
  position: relative;
  display: inline-block;
  width: 100%;
`;

const NONE_VALUE = "";

const RoleRateDetails = ({
  roleRate: initialRoleRate,
  divisions,
  consultants,
  venues = []
}) => {
  const [roleRate, setRoleRate] = useState(initialRoleRate);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    setRoleRate(initialRoleRate);
  }, [initialRoleRate]);

  const [updateRoleRate, { loading: isSaving }] = useMutation(UPDATE_ROLE_RATE);

  const { showToast } = useToast();

  const defaultVenueOptions = [
    { label: "None", value: NONE_VALUE },
    ...venues.map(v => ({ label: v.name, value: v.id }))
  ];

  const handleChange = (field, value) => {
    setRoleRate(prev => {
      const newRoleRate = { ...prev };
      if (field === "division") {
        newRoleRate.division = { ...prev.division, id: value };
      } else if (field === "consultant") {
        newRoleRate.consultant = { ...prev.consultant, id: value };
      } else if (field === "defaultVenueId") {
        newRoleRate.defaultVenueId = value === NONE_VALUE ? null : value;
      } else {
        newRoleRate[field] = value;
      }

      return newRoleRate;
    });
    setHasChanges(true);
  };

  const handleSave = async () => {
    try {
      await updateRoleRate({
        variables: {
          roleRateId: roleRate.id,
          divisionId: roleRate.division?.id,
          consultantId: roleRate.consultant?.id,
          sageRef: roleRate.sageRef,
          canCandidatesEditBreaks: roleRate.canCandidatesEditBreaks,
          canRebookPastShifts: roleRate.canRebookPastShifts,
          isOnCallShiftsEnabled: roleRate.isOnCallShiftsEnabled,
          isReservedBookingsEnabled: roleRate.isReservedBookingsEnabled,
          defaultVenueId: roleRate.defaultVenueId || null
        }
      });

      setHasChanges(false);
      showToast("Changes saved successfully.", { severity: "success" });
    } catch (error) {
      showToast(error.message, { severity: "error" });
    }
  };

  const defaultVenueDropdownValue =
    roleRate.defaultVenueId != null ? roleRate.defaultVenueId : NONE_VALUE;

  return (
    <ContentWrapper>
      <StyledGrid>
        <LabelCell>Division Name</LabelCell>
        <LabelCell>Division Assignee</LabelCell>
        <LabelCell>Division Sage Reference</LabelCell>
        <ValueCell>
          <DropdownWrapper>
            <RotaDropdown
              value={roleRate.division?.id}
              placeholder="Select a division"
              options={divisions.map(div => ({
                label: div.name,
                value: div.id
              }))}
              onChange={value => handleChange("division", value)}
            />
          </DropdownWrapper>
        </ValueCell>
        <ValueCell>
          <DropdownWrapper>
            <RotaDropdown
              value={roleRate.consultant?.id}
              placeholder="Select an assignee"
              options={consultants.map(cons => ({
                label: `${cons.firstName} ${cons.lastName}`,
                value: cons.id
              }))}
              onChange={value => handleChange("consultant", value)}
            />
          </DropdownWrapper>
        </ValueCell>
        <ValueCell>
          <RotaInput
            autocomplete="off"
            value={roleRate.sageRef}
            onChange={e => handleChange("sageRef", e.target.value)}
          />
        </ValueCell>
      </StyledGrid>
      <StyledGrid>
        <LabelCell>Default Venue</LabelCell>
        <LabelCell />
        <LabelCell />
        <ValueCell>
          <DropdownWrapper>
            <RotaDropdown
              value={defaultVenueDropdownValue}
              placeholder="Select default venue"
              options={defaultVenueOptions}
              onChange={value => handleChange("defaultVenueId", value)}
            />
          </DropdownWrapper>
        </ValueCell>
        <ValueCell />
        <ValueCell />
      </StyledGrid>
      <StyledGrid>
        <LabelCell>Enable Candidates To Edit Break Times</LabelCell>
        <LabelCell>Enable on-call Shifts</LabelCell>
        <LabelCell>Enable Reserved Bookings</LabelCell>
        <ValueCell isCenterAligned>
          <RotaCheckbox
            isChecked={roleRate.canCandidatesEditBreaks}
            isDisabled={false}
            onClick={() =>
              handleChange(
                "canCandidatesEditBreaks",
                !roleRate.canCandidatesEditBreaks
              )
            }
          />
        </ValueCell>
        <ValueCell isCenterAligned>
          <RotaCheckbox
            isChecked={roleRate.isOnCallShiftsEnabled}
            isDisabled={false}
            onClick={() =>
              handleChange(
                "isOnCallShiftsEnabled",
                !roleRate.isOnCallShiftsEnabled
              )
            }
          />
        </ValueCell>
        <ValueCell isCenterAligned>
          <RotaCheckbox
            isChecked={roleRate.isReservedBookingsEnabled}
            isDisabled={false}
            onClick={() =>
              handleChange(
                "isReservedBookingsEnabled",
                !roleRate.isReservedBookingsEnabled
              )
            }
          />
        </ValueCell>
      </StyledGrid>
      <StyledGrid>
        <LabelCell>Allow Rebook From This Monday</LabelCell>
        <LabelCell />
        <LabelCell />
        <ValueCell isCenterAligned>
          <RotaCheckbox
            isChecked={!!roleRate.canRebookPastShifts}
            isDisabled={false}
            onClick={() => handleChange("canRebookPastShifts", !roleRate.canRebookPastShifts)}
          />
        </ValueCell>
        <ValueCell />
        <ValueCell />
      </StyledGrid>
      <ButtonContainer>
        <RotaButton onClick={handleSave} disabled={isSaving || !hasChanges}>
          Save Changes
        </RotaButton>
      </ButtonContainer>
    </ContentWrapper>
  );
};

export default RoleRateDetails;
